import { TextTypeEnum, type TextBoxParams, type TextParagraphRun, type TextStyleRun } from './custom-template';

/**
 * Per-run sizes that ride the box font scale: fontSize, leading, baselineShift and the glyph outline width share canvas
 * px. The outline width is a PSD TextStyle size like the font's; the SVG text renderer strokes the box's one at the
 * glyph scale, so a run's own width must grow with its letters too.
 */
export function scaleStyleRunSizes(runs: readonly TextStyleRun[], scale: number): TextStyleRun[] {
  return runs.map(r => ({
    ...r,
    ...(r.fontSize !== undefined && { fontSize: r.fontSize * scale }),
    ...(r.lineHeightPx !== undefined && { lineHeightPx: r.lineHeightPx * scale }),
    ...(r.baselineShift !== undefined && { baselineShift: r.baselineShift * scale }),
    ...(r.outlineWidth !== undefined && { outlineWidth: r.outlineWidth * scale }),
  }));
}

/** Paragraph px fields: indents and gaps, on the box and inside every paragraph run. */
const PARAGRAPH_PX = ['firstLineIndent', 'startIndent', 'endIndent', 'spaceBefore', 'spaceAfter'] as const;

/** The px fields of a text bag that ride the font scale — the same set on the box params and on the drawable bag. */
export type TextBagSizes = Partial<
  Pick<TextBoxParams, (typeof PARAGRAPH_PX)[number] | 'leading' | 'styleRuns' | 'paragraphRuns'>
>;

function scaleParagraphRun(run: TextParagraphRun, scale: number): TextParagraphRun {
  const next: TextParagraphRun = { ...run };
  for (const key of PARAGRAPH_PX) if (run[key] !== undefined) next[key] = (run[key] as number) * scale;
  return next;
}

/**
 * One law for every form that rescales a text box (template projection, cover refit, sheet rescale): the bag's canvas px
 * — leading, paragraph indents and gaps, run sizes, paragraph-run indents and gaps — ride the font scale. Only the
 * keys present come back, so a spread over the source changes nothing else. `quoteFloorFontSize` is a size of the
 * block, not of the bag: the caller scales it with `fontSize`.
 */
export function scaleTextBagSizes(bag: TextBagSizes, scale: number): TextBagSizes {
  const next: TextBagSizes = {};
  for (const key of PARAGRAPH_PX) if (bag[key] !== undefined) next[key] = (bag[key] as number) * scale;
  if (bag.leading != null) next.leading = bag.leading * scale;
  if (bag.styleRuns) next.styleRuns = scaleStyleRunSizes(bag.styleRuns, scale);
  if (bag.paragraphRuns) next.paragraphRuns = bag.paragraphRuns.map(run => scaleParagraphRun(run, scale));
  return next;
}

/** Run props the engine draws per run; the rest it takes from the box (glyph_layout.rs run props). */
const DRAWN = [
  'fontSize',
  'color',
  'tracking',
  'fontCaps',
  'fontBaseline',
  'horizontalScale',
  'verticalScale',
  'baselineShift',
] as const;
type Drawn = (typeof DRAWN)[number];
/** Absent value = this; the parser mapper drops zero caps/baseline/shift. */
const NEUTRAL: Partial<Record<Drawn, number>> = {
  tracking: 0,
  fontCaps: 0,
  fontBaseline: 0,
  baselineShift: 0,
  horizontalScale: 1,
  verticalScale: 1,
};
/** Box slots of a split part dropped on every part / on parts after the first. */
const PART_DROPPED = ['verticalAlign'] as const;
const TAIL_DROPPED = [
  'firstLineIndent',
  'vignetteQuote',
  'quoteNominalCapacity',
  'quoteFloorCapacity',
  'quoteFloorFontSize',
  'leadLine',
  'memoryEntry',
] as const;

export type TextPart = { from: number; to: number };
type Fields = Record<string, unknown>;

const runAt = (p: TextBoxParams, i: number): TextStyleRun | undefined => p.styleRuns?.find(r => r.from <= i && i < r.to);

const drawnValue = (p: TextBoxParams, run: TextStyleRun | undefined, k: Drawn): unknown =>
  run?.[k] ?? (p as Fields)[k] ?? NEUTRAL[k];

const drawnKey = (p: TextBoxParams, i: number): string => {
  const run = runAt(p, i);
  return JSON.stringify(DRAWN.map(k => drawnValue(p, run, k)));
};

/**
 * Parts of a text that differ in drawn style, cut only at `\n` (the engine breaks lines only there).
 * Equal neighbour paragraphs merge; a paragraph mixing styles is its own part; empty paragraphs and the trailing
 * break ride the part above. Fewer than two parts → `[]`.
 */
export function textStyleParts(params: TextBoxParams): TextPart[] {
  const text = params.text ?? '';
  if (!params.styleRuns?.length) return [];
  const parts: (TextPart & { style: string | null })[] = [];
  let start = 0;
  for (const line of text.split('\n')) {
    const end = start + line.length;
    if (line.length) {
      const styles = new Set<string>();
      for (let i = start; i < end; i++) styles.add(drawnKey(params, i));
      const style = styles.size === 1 ? [...styles][0] : null;
      const prev = parts[parts.length - 1];
      if (!prev || style === null || prev.style !== style) {
        if (prev) prev.to = start;
        parts.push({ from: parts.length ? start : 0, to: text.length, style });
      }
    }

    start = end + 1;
  }

  return parts.length < 2 ? [] : parts.map(({ from, to }) => ({ from, to }));
}

/** Layout is line-based on the box rect: no path, warp, vertical writing, auto-fit or area shape. */
export function textSplittable(params: TextBoxParams): boolean {
  return !params.textPath && !params.warp && params.orientation !== 'vertical' && !params.autoFit && !params.areaShape?.d;
}

/**
 * Box params of one part: own text, the first run's drawn style on the box, runs clipped + rebased. Part 1 keeps the
 * role; later parts are free text — a quote's tail pinned to `block`, the mode the quote role drew it in.
 */
export function textPartParams(params: TextBoxParams, part: TextPart, index: number): TextBoxParams {
  const text = (params.text ?? '').slice(part.from, part.to).replace(/\n$/, '');
  const out: Fields = { ...params, text };
  const first = runAt(params, part.from);
  for (const k of DRAWN) {
    // Box has no baselineShift slot: it stays on the runs.
    if (k === 'baselineShift') continue;
    const v = first?.[k] ?? (params as Fields)[k];
    if (v !== undefined) out[k] = v;
  }

  if (params.fontSizePt !== undefined && out['fontSize'] !== params.fontSize) {
    out['fontSizePt'] = (params.fontSizePt * (out['fontSize'] as number)) / params.fontSize;
  }

  for (const k of PART_DROPPED) delete out[k];
  if (index >= 1) {
    for (const k of TAIL_DROPPED) delete out[k];
    out['textType'] = TextTypeEnum.FREE_TEXT;
    delete out['fioLayout'];
    if (params.textType === TextTypeEnum.PERSON_QUOTE) out['textMode'] = 'block';
  }

  if (params.text === params.templateText) out['templateText'] = text;
  else delete out['templateText'];

  // Chars outside any run draw the SOURCE box style; spell it out where the part box now differs.
  const src = [...(params.styleRuns ?? [])].filter(r => r.to > part.from && r.from < part.to).sort((a, b) => a.from - b.from);
  const clipped: Fields[] = [];
  let at = part.from;
  for (const r of src) {
    if (r.from > at) clipped.push({ from: at, to: r.from });
    clipped.push({ ...r, from: Math.max(r.from, part.from), to: Math.min(r.to, part.to) });
    at = Math.min(r.to, part.to);
  }

  if (at < part.to) clipped.push({ from: at, to: part.to });

  const runs: TextStyleRun[] = [];
  for (const r of clipped) {
    for (const k of DRAWN) if (r[k] === undefined) r[k] = (params as Fields)[k] ?? NEUTRAL[k];
    for (const k of Object.keys(r)) {
      if (k === 'from' || k === 'to') continue;
      const boxValue = (DRAWN as readonly string[]).includes(k) ? (out[k] ?? NEUTRAL[k as Drawn]) : out[k];
      if (r[k] === undefined || r[k] === boxValue) delete r[k];
    }

    const from = (r['from'] as number) - part.from;
    const to = Math.min((r['to'] as number) - part.from, text.length);
    if (to > from && Object.keys(r).length > 2) runs.push({ ...(r as TextStyleRun), from, to });
  }

  out['styleRuns'] = runs.length ? runs : undefined;
  return out as TextBoxParams;
}
